import { sortByName } from '../utils/sortOptions';
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import { PlusIcon, XMarkIcon, MegaphoneIcon } from '@heroicons/react/24/outline';

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .on-root * { box-sizing:border-box; }
  .on-root { font-family:'Sora',sans-serif; color:#1a1d2e; animation:onFade .4s ease both; }
  @keyframes onFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .on-header { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.2rem; flex-wrap:wrap; gap:1rem; }
  .on-title  { font-size:1.35rem; font-weight:700; letter-spacing:-.02em; color:#0f1123; }
  .on-sub    { font-size:.76rem; color:#9094a8; margin-top:3px; }
  .btn { display:inline-flex; align-items:center; gap:7px; padding:9px 16px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .btn svg { width:15px; height:15px; stroke-width:2; }
  .btn-blue  { background:#3b6ef8; color:white; }
  .btn-blue:hover  { background:#2d5ef0; box-shadow:0 4px 14px rgba(59,110,248,.35); }
  .btn-ghost { background:#f0f2f7; color:#6b7194; }
  .btn-ghost:hover { background:#e4e7f0; }
  .btn-sm { padding:6px 12px; font-size:.74rem; }
  .btn:disabled { opacity:.5; cursor:not-allowed; }
  .panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; }
  .panel-hd { padding:14px 18px; border-bottom:1px solid #f0f2f8; font-size:.86rem; font-weight:600; }
  .on-empty { text-align:center; padding:3rem 2rem; color:#9094a8; font-size:.82rem; }
  .on-loading { display:flex; align-items:center; justify-content:center; height:160px; gap:10px; color:#9094a8; font-size:.86rem; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#3b6ef8; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg);} }
  .modal-backdrop { position:fixed; inset:0; background:rgba(15,17,35,.55); backdrop-filter:blur(4px); display:flex; align-items:center; justify-content:center; z-index:1000; padding:20px; }
  .modal { background:white; border-radius:20px; padding:26px; width:100%; max-width:480px; max-height:90vh; overflow-y:auto; box-shadow:0 20px 60px rgba(0,0,0,.2); }
  .modal-head  { display:flex; align-items:center; justify-content:space-between; margin-bottom:18px; }
  .modal-title { font-size:1rem; font-weight:700; color:#0f1123; }
  .modal-close { width:30px; height:30px; border-radius:8px; border:none; background:#f0f2f7; color:#6b7194; cursor:pointer; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .modal-btns { display:flex; gap:8px; justify-content:flex-end; margin-top:20px; flex-wrap:wrap; }
  .form-field { display:flex; flex-direction:column; gap:5px; margin-bottom:14px; }
  .form-label { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .form-input,.form-select,.form-textarea { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%; }
  .form-textarea { resize:vertical; min-height:80px; }
  @media (max-width:768px) {
    .on-title { font-size:1.1rem; }
    .modal-btns { justify-content:stretch; }
    .modal-btns .btn { flex:1; justify-content:center; }
  }
`;

const fmtDate = (d) => new Date(d).toLocaleString('en-US',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'});

const OwnerNotices = () => {
  const [schools,  setSchools]  = useState([]);
  const [schoolId, setSchoolId] = useState('');
  const [notices,  setNotices]  = useState([]);
  const [admins,   setAdmins]   = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [open,     setOpen]     = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [mode,     setMode]     = useState('broadcast'); // 'broadcast' | 'specific'
  const [form,     setForm]     = useState({ title:'', content:'', targetAdminId:'' });

  useEffect(() => {
    api.get('/owner/schools').then(r => {
      const s = r.data?.schools || r.data || [];
      setSchools(Array.isArray(s) ? s : []);
      if (s.length) setSchoolId(s[0]._id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!schoolId) return;
    api.get(`/owner/branches/${schoolId}/admins`).then(r => setAdmins(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    fetchNotices();
  }, [schoolId]);

  const fetchNotices = async () => {
    if (!schoolId) return;
    setLoading(true);
    try { const { data } = await api.get(`/owner/branches/${schoolId}/notices`); setNotices(Array.isArray(data) ? data : []); }
    catch { toast.error('Failed to load notices'); }
    finally { setLoading(false); }
  };

  const openModal = () => {
    setForm({ title:'', content:'', targetAdminId:'' });
    setMode('broadcast');
    setOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.title || !form.content) { toast.error('Title and content required'); return; }
    if (mode === 'specific' && !form.targetAdminId) { toast.error('Select an admin'); return; }
    setSaving(true);
    try {
      await api.post(`/owner/branches/${schoolId}/notices`, {
        title: form.title, content: form.content,
        targetAdminId: mode === 'specific' ? form.targetAdminId : undefined,
      });
      toast.success('Notice posted');
      setOpen(false);
      fetchNotices();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="on-root">
      <style>{style}</style>

      <div style={{marginBottom:'1rem'}}>
        <select value={schoolId} onChange={e => setSchoolId(e.target.value)}
          style={{minWidth:220,padding:'8px 12px',border:'1.5px solid #e0e4f0',borderRadius:10,fontFamily:'Sora',fontSize:'.82rem',background:'white',outline:'none'}}>
          <option value="">Select School…</option>
          {sortByName(schools).map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
        </select>
      </div>

      <div className="on-header">
        <div>
          <div className="on-title">Notices to Admins</div>
          <div className="on-sub">{notices.length} sent · broadcast to all admins or a specific one</div>
        </div>
        <button className="btn btn-blue" onClick={openModal} disabled={!schoolId}><PlusIcon />Post Notice</button>
      </div>

      <div className="panel">
        <div className="panel-hd">All Notices ({notices.length})</div>
        {loading ? <div className="on-loading"><div className="spinner"/>Loading…</div>
        : !schoolId ? <div className="on-empty">Select a school</div>
        : !notices.length ? <div className="on-empty">No notices yet.</div>
        : <div style={{padding:'10px 18px'}}>
            {notices.map(n => (
              <div key={n._id} style={{padding:'12px 0',borderBottom:'1px solid #f0f2f8'}}>
                <div style={{fontWeight:700,fontSize:'.86rem',color:'#0f1123',marginBottom:4}}>{n.title}</div>
                <div style={{fontSize:'.8rem',color:'#4a5075',lineHeight:1.6,marginBottom:6}}>{n.content}</div>
                <div style={{fontSize:'.7rem',color:'#9094a8'}}>{fmtDate(n.createdAt || n.created_at)}</div>
              </div>
            ))}
          </div>}
      </div>

      {open && createPortal(
        <div className="modal-backdrop" onClick={() => setOpen(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-head">
              <div className="modal-title">Post Notice to Admins</div>
              <button className="modal-close" onClick={() => setOpen(false)}><XMarkIcon /></button>
            </div>
            <div className="form-field">
              <label className="form-label">Title</label>
              <input className="form-input" value={form.title} onChange={e=>setForm({...form,title:e.target.value})} placeholder="Notice title…" />
            </div>
            <div className="form-field">
              <label className="form-label">Content</label>
              <textarea className="form-textarea" value={form.content} onChange={e=>setForm({...form,content:e.target.value})} placeholder="Write the notice…" />
            </div>
            <div className="form-field">
              <label className="form-label">Send To</label>
              <div style={{display:'flex',gap:6}}>
                <button type="button" className={`btn ${mode==='broadcast'?'btn-blue':'btn-ghost'} btn-sm`} style={{flex:1,justifyContent:'center'}} onClick={()=>setMode('broadcast')}>All Admins</button>
                <button type="button" className={`btn ${mode==='specific'?'btn-blue':'btn-ghost'} btn-sm`} style={{flex:1,justifyContent:'center'}} onClick={()=>setMode('specific')}>Specific Admin</button>
              </div>
            </div>
            {mode === 'specific' && (
              <div className="form-field">
                <label className="form-label">Admin</label>
                <select className="form-select" value={form.targetAdminId} onChange={e=>setForm({...form,targetAdminId:e.target.value})}>
                  <option value="">Select admin…</option>
                  {admins.map(a => <option key={a._id} value={a._id}>{a.name} — {a.email}</option>)}
                </select>
              </div>
            )}
            <div className="modal-btns">
              <button className="btn btn-ghost" onClick={() => setOpen(false)}>Cancel</button>
              <button className="btn btn-blue" onClick={handleSubmit} disabled={saving}><MegaphoneIcon />{saving?'Posting…':'Post Notice'}</button>
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  );
};

export default OwnerNotices;
